/**
 * Alert Component
 *
 * A reusable alert/dialog component for displaying messages to users
 * with configurable buttons and callbacks.
 *
 * Features:
 * - Multiple alert types (error, warning, info, success)
 * - Configurable buttons with custom labels and callbacks
 * - Accessible (built on Radix Dialog)
 * - Themeable with existing design system
 * - Type-safe button configurations
 *
 * @example
 * ```tsx
 * <Alert
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   type="error"
 *   title="Cannot Move Card"
 *   message="The 'In Progress' column can only hold 3 cards at a time."
 *   buttons={[
 *     { label: 'Got it', onClick: () => setIsOpen(false) }
 *   ]}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <Alert
 *   open={isDeleteWarningOpen}
 *   onOpenChange={setIsDeleteWarningOpen}
 *   type="warning"
 *   title="Delete Card?"
 *   message="This action cannot be undone."
 *   buttons={[
 *     { label: 'Cancel', variant: 'outline', onClick: () => setIsDeleteWarningOpen(false) },
 *     { label: 'Delete', variant: 'destructive', onClick: handleDelete }
 *   ]}
 * />
 * ```
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Icon, IconName } from '@/icons';
import { cn } from '@/lib/utils';
import { useThemeColors } from '@/styles';

/**
 * Button configuration for Alert component
 */
export interface AlertButton {
  /** Button label text */
  label: string;
  /** Button variant (default: 'default' for first button, 'outline' for others) */
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  /** Callback function when button is clicked */
  onClick: () => void;
  /** Optional: Auto-close alert after button click (default: true) */
  autoClose?: boolean;
}

/**
 * Alert type variants
 */
export type AlertType = 'error' | 'warning' | 'info' | 'success';

/**
 * Alert component props
 */
export interface AlertProps {
  /** Whether the alert is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Alert type (affects icon and styling) */
  type?: AlertType;
  /** Alert title */
  title: string;
  /** Alert message/description */
  message: string;
  /** Button configurations */
  buttons?: AlertButton[];
  /** Optional: Custom icon to override type-based icon */
  customIcon?: React.ReactNode;
  /** Optional: Content rendered between message and buttons */
  footer?: React.ReactNode;
  /** Optional: Additional class name */
  className?: string;
}

/**
 * Get icon and color based on alert type
 */
function getAlertTypeConfig(
  type: AlertType,
  colors: ReturnType<typeof useThemeColors>
): {
  icon: IconName;
  iconColor: string;
  iconBgColor: string;
} {
  switch (type) {
    case 'error':
      return {
        icon: IconName.ALERT_CIRCLE,
        iconColor: colors.error,
        iconBgColor: colors.error + '20',
      };
    case 'warning':
      return {
        icon: IconName.ALERT_CIRCLE,
        iconColor: colors.rust, // Using rust as warning color
        iconBgColor: colors.rust + '20',
      };
    case 'info':
      return {
        icon: IconName.INFO,
        iconColor: colors.blue,
        iconBgColor: colors.blue + '20',
      };
    case 'success':
      return {
        icon: IconName.CHECK,
        iconColor: colors.success,
        iconBgColor: colors.success + '20',
      };
  }
}

/**
 * Alert Component
 *
 * Displays a modal alert dialog with configurable buttons and callbacks.
 */
export function Alert({
  open,
  onOpenChange,
  type = 'info',
  title,
  message,
  buttons = [{ label: 'OK', onClick: () => onOpenChange(false) }],
  customIcon,
  footer,
  className,
}: AlertProps) {
  const colors = useThemeColors();
  const typeConfig = getAlertTypeConfig(type, colors);

  /**
   * Handle button click with auto-close logic
   */
  const handleButtonClick = (button: AlertButton) => {
    button.onClick();

    // Auto-close unless explicitly disabled
    if (button.autoClose !== false) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-md', className)}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          {customIcon || (
            <div
              className="rounded-full p-3"
              style={{ backgroundColor: typeConfig.iconBgColor }}
            >
              <Icon
                name={typeConfig.icon}
                size="lg"
                style={{ color: typeConfig.iconColor }}
              />
            </div>
          )}
        </div>

        {/* Header */}
        <DialogHeader>
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center">
            {message}
          </DialogDescription>
        </DialogHeader>

        {/* Custom footer content */}
        {footer && (
          <div className="flex justify-center mt-1 mb-2">{footer}</div>
        )}

        {/* Footer with buttons */}
        <DialogFooter className="sm:justify-center gap-2">
          {buttons.map((button, index) => (
            <Button
              key={index}
              variant={button.variant || (index === 0 ? 'default' : 'outline')}
              onClick={() => handleButtonClick(button)}
            >
              {button.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook for managing alert state
 *
 * Provides a convenient way to manage alert open/close state
 * and display alerts programmatically.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const alert = useAlert()
 *
 *   const handleAction = () => {
 *     alert.show({
 *       type: 'error',
 *       title: 'Error',
 *       message: 'Something went wrong',
 *       buttons: [{ label: 'OK', onClick: () => {} }]
 *     })
 *   }
 *
 *   return (
 *     <>
 *       <button onClick={handleAction}>Do Something</button>
 *       <Alert {...alert.props} />
 *     </>
 *   )
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with component
export function useAlert() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [alertConfig, setAlertConfig] = React.useState<
    Omit<AlertProps, 'open' | 'onOpenChange'>
  >({
    title: '',
    message: '',
    type: 'info',
    buttons: [{ label: 'OK', onClick: () => {} }],
  });

  const show = (config: Omit<AlertProps, 'open' | 'onOpenChange'>) => {
    setAlertConfig(config);
    setIsOpen(true);
  };

  const hide = () => {
    setIsOpen(false);
  };

  return {
    show,
    hide,
    isOpen,
    props: {
      ...alertConfig,
      open: isOpen,
      onOpenChange: setIsOpen,
    },
  };
}
